import { useState } from 'react';
import { Button } from '@/components/ui';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Combobox, type ComboboxOption } from '@/components/ui/combobox';
import { ChevronUp, ChevronDown, Plus, X, Settings2 } from 'lucide-react';
import { useIqServices } from '@/hooks/use-iq-services';
import type { FallbackChainEntry, FlowType } from '@/types/service';

const CHAIN_GATEWAY_ID_PATTERN = /^[a-zA-Z][a-zA-Z0-9_]{2,99}$/;
const IQ_SERVICE_ID_PATTERN = /^iq_[a-z0-9]/;
const MAX_ENTRIES = 5;

interface FallbackChainEditorProps {
  value: FallbackChainEntry[];
  onChange: (chain: FallbackChainEntry[]) => void;
  errors?: Record<string, string>;
  disabled?: boolean;
}

/**
 * Editor fuer die Fallback-Kette (Spec 0128).
 * 1-5 Eintraege, Reorder per Pfeiltasten, FlowType-Dropdown + gatewayServiceId-Combobox.
 */
export function FallbackChainEditor({
  value,
  onChange,
  errors = {},
  disabled = false,
}: FallbackChainEditorProps) {
  // Track which entries have the "advanced" toggle open
  const [expandedAdvanced, setExpandedAdvanced] = useState<Set<number>>(new Set());

  // IQ-Services-Daten fuer Comboboxen
  const { data: iqServicesData, isLoading: iqLoading } = useIqServices();

  // Combobox-Optionen aus IQ-Services ableiten
  const iqServiceOptions: ComboboxOption[] = (iqServicesData?.data ?? []).map((svc) => ({
    value: svc.iqServiceId,
    label: svc.displayName,
    description: `${svc.iqServiceId} · ${svc.merchantName} · ${svc.gatewayMode}`,
  }));

  const addEntry = () => {
    if (value.length >= MAX_ENTRIES) return;
    onChange([...value, { flowType: '' as FlowType, gatewayServiceId: '' }]);
  };

  const removeEntry = (index: number) => {
    if (value.length <= 1) return;
    const next = value.filter((_, i) => i !== index);
    onChange(next);
    // Clean up expanded state
    setExpandedAdvanced(prev => {
      const updated = new Set<number>();
      prev.forEach(i => {
        if (i < index) updated.add(i);
        else if (i > index) updated.add(i - 1);
      });
      return updated;
    });
  };

  const updateEntry = (index: number, field: keyof FallbackChainEntry, val: string) => {
    const next = value.map((entry, i) =>
      i === index ? { ...entry, [field]: val || undefined } : entry
    );
    // Keep flowType as string even if empty (for the dropdown placeholder)
    if (field === 'flowType') {
      next[index] = { ...next[index], flowType: val as FlowType };
    }
    onChange(next);
  };

  const moveEntry = (index: number, direction: 'up' | 'down') => {
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= value.length) return;
    const next = [...value];
    [next[index], next[targetIndex]] = [next[targetIndex], next[index]];
    onChange(next);
  };

  const toggleAdvanced = (index: number) => {
    setExpandedAdvanced(prev => {
      const updated = new Set(prev);
      if (updated.has(index)) {
        updated.delete(index);
      } else {
        updated.add(index);
      }
      return updated;
    });
  };

  const getFieldError = (index: number, field: string): string | undefined => {
    return errors[`${index}.${field}`] || errors[`fallbackChain[${index}].${field}`];
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <Label className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          Fallback-Kette
        </Label>
        <span className="text-xs text-muted-foreground">
          {value.length}/{MAX_ENTRIES} Einträge
        </span>
      </div>

      {errors.fallbackChain && (
        <p className="text-[13px] text-destructive">{errors.fallbackChain}</p>
      )}

      {value.map((entry, index) => (
        <div
          key={index}
          className={`rounded-lg border p-3 ${index === 0 ? 'border-primary/40 bg-primary/5' : 'border-border'} ${disabled ? 'opacity-60' : ''}`}
        >
          {/* Header: Priority badge + move/remove buttons */}
          <div className="mb-2 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-muted-foreground">#{index + 1}</span>
              {index === 0 && (
                <Badge variant="secondary" className="text-xs">
                  Primär
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-1">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0"
                onClick={() => moveEntry(index, 'up')}
                disabled={disabled || index === 0}
                title="Nach oben"
              >
                <ChevronUp className="h-4 w-4" />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0"
                onClick={() => moveEntry(index, 'down')}
                disabled={disabled || index === value.length - 1}
                title="Nach unten"
              >
                <ChevronDown className="h-4 w-4" />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0 text-destructive hover:text-destructive"
                onClick={() => removeEntry(index)}
                disabled={disabled || value.length <= 1}
                title={value.length <= 1 ? 'Mindestens 1 Eintrag erforderlich' : 'Entfernen'}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* FlowType + gatewayServiceId */}
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <Label htmlFor={`chain-flow-${index}`} className="text-xs">
                Flow-Typ <span className="text-destructive">*</span>
              </Label>
              <Select
                value={entry.flowType || ''}
                onValueChange={(val) => updateEntry(index, 'flowType', val)}
                disabled={disabled}
              >
                <SelectTrigger
                  id={`chain-flow-${index}`}
                  className={getFieldError(index, 'flowType') ? 'border-destructive' : ''}
                >
                  <SelectValue placeholder="Flow-Typ wählen" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="WAP_CLICK">WAP_CLICK</SelectItem>
                  <SelectItem value="WEB_TAN">WEB_TAN</SelectItem>
                </SelectContent>
              </Select>
              {getFieldError(index, 'flowType') && (
                <p className="text-[12px] text-destructive">{getFieldError(index, 'flowType')}</p>
              )}
            </div>

            <div className="space-y-1">
              <Label htmlFor={`chain-gw-${index}`} className="text-xs">
                Gateway-Service-ID <span className="text-destructive">*</span>
              </Label>
              <Combobox
                id={`chain-gw-${index}`}
                options={iqServiceOptions}
                value={entry.gatewayServiceId}
                onChange={(val) => updateEntry(index, 'gatewayServiceId', val)}
                placeholder="Service wählen oder eingeben"
                searchPlaceholder="IQ-Service suchen…"
                emptyText="Kein passender Service"
                allowFreeText
                disabled={disabled}
                loading={iqLoading}
                error={!!getFieldError(index, 'gatewayServiceId')}
              />
              {getFieldError(index, 'gatewayServiceId') && (
                <p className="text-[12px] text-destructive">{getFieldError(index, 'gatewayServiceId')}</p>
              )}
            </div>
          </div>

          {/* Advanced: iqServiceId (toggle) */}
          <div className="mt-2">
            <button
              type="button"
              className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
              onClick={() => toggleAdvanced(index)}
              disabled={disabled}
            >
              <Settings2 className="h-3 w-3" />
              Erweitert
            </button>
            {expandedAdvanced.has(index) && (
              <div className="mt-2 space-y-1">
                <Label htmlFor={`chain-iq-${index}`} className="text-xs">
                  IQ-Service-ID (optional)
                </Label>
                <Combobox
                  id={`chain-iq-${index}`}
                  options={iqServiceOptions}
                  value={entry.iqServiceId ?? ''}
                  onChange={(val) => updateEntry(index, 'iqServiceId', val)}
                  placeholder="IQ-Service wählen oder eingeben"
                  searchPlaceholder="IQ-Service suchen…"
                  emptyText="Kein passender Service"
                  allowFreeText
                  disabled={disabled}
                  loading={iqLoading}
                  error={!!getFieldError(index, 'iqServiceId')}
                />
                {getFieldError(index, 'iqServiceId') && (
                  <p className="text-[12px] text-destructive">{getFieldError(index, 'iqServiceId')}</p>
                )}
              </div>
            )}
          </div>
        </div>
      ))}

      {/* Add button */}
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={addEntry}
        disabled={disabled || value.length >= MAX_ENTRIES}
        className="w-full"
      >
        <Plus className="mr-1 h-4 w-4" />
        {value.length >= MAX_ENTRIES
          ? `Maximal ${MAX_ENTRIES} Einträge erlaubt`
          : 'Eintrag hinzufügen'}
      </Button>
    </div>
  );
}

/**
 * Client-seitige Validierung der Fallback-Kette.
 * Gibt Record<string, string> mit Fehler-Keys zurueck.
 */
export function validateFallbackChain(
  chain: FallbackChainEntry[]
): Record<string, string> {
  const errors: Record<string, string> = {};

  if (chain.length === 0) {
    errors.fallbackChain = 'Mindestens 1 Eintrag erforderlich';
    return errors;
  }

  const seenGatewayIds = new Set<string>();

  for (let i = 0; i < chain.length; i++) {
    const entry = chain[i];

    if (!entry.flowType) {
      errors[`${i}.flowType`] = 'Flow-Typ ist erforderlich';
    }

    if (!entry.gatewayServiceId) {
      errors[`${i}.gatewayServiceId`] = 'Gateway-Service-ID ist erforderlich';
    } else if (!CHAIN_GATEWAY_ID_PATTERN.test(entry.gatewayServiceId)) {
      errors[`${i}.gatewayServiceId`] = 'Muss mit Buchstabe beginnen (3-100 Zeichen)';
    } else if (seenGatewayIds.has(entry.gatewayServiceId)) {
      errors[`${i}.gatewayServiceId`] = 'Muss innerhalb der Kette eindeutig sein';
    } else {
      seenGatewayIds.add(entry.gatewayServiceId);
    }

    if (entry.iqServiceId && !IQ_SERVICE_ID_PATTERN.test(entry.iqServiceId)) {
      errors[`${i}.iqServiceId`] = 'Muss mit iq_ beginnen';
    }
  }

  return errors;
}

/**
 * Parsed Server-Fehler-Details in Chain-Editor-kompatible Error-Keys.
 */
export function parseChainServerErrors(
  details?: Array<{ field: string; issue?: string; message?: string }>
): Record<string, string> {
  const errors: Record<string, string> = {};
  if (!details) return errors;

  const chainPattern = /fallbackChain\[(\d+)\]\.(\w+)/;
  for (const detail of details) {
    const match = chainPattern.exec(detail.field);
    if (match) {
      errors[`${match[1]}.${match[2]}`] = detail.issue || detail.message || 'Ungültiger Wert';
    }
  }

  return errors;
}
